import React from "react";
import { LogIn, Activity, ShieldCheck, Zap, TrendingUp, Brain } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#FFCC00]/[0.05] via-transparent to-[#F472B6]/[0.05]" />

      <div className="relative max-w-6xl mx-auto px-6 py-10 min-h-screen flex flex-col">
        {/* Top brand */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="login-brand">
            <div className="relative w-10 h-10 rounded-md bg-[#FFCC00] flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-5 h-5 text-black"><path d="M3 17 L9 11 L13 14 L21 6" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-bold text-lg tracking-tight">TradingExpert AI</span>
              <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">India + US Intraday · NSE • NASDAQ • NYSE</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-400 px-3 py-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> Two markets · one AI desk
          </div>
        </div>

        {/* Hero */}
        <div className="flex-1 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center mt-12">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFCC00] mb-3">// India + US · AI Intraday Desk</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              Two markets.<br />
              <span className="text-[#FFCC00]">One intraday AI</span> desk.
            </h1>
            <p className="text-zinc-400 mt-5 text-lg max-w-xl">
              TradingExpert AI runs autonomous, adaptive-learning GPT-5.2 engines that scan the live tape, decide entries with tight SL/TP, size positions, track live P&L, and auto square-off before the close — on <span className="text-white">NSE cash equities (India)</span> and <span className="text-white">US stocks & ETFs</span>.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="login-google-btn"
                onClick={handleLogin}
                className="group relative px-6 py-3.5 bg-[#FFCC00] text-black rounded-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#000" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#000" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#000" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#000" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              <div className="px-4 py-3.5 flex items-center gap-2 text-xs font-mono text-zinc-500 border border-zinc-900 rounded-sm">
                <ShieldCheck className="w-4 h-4 text-[#FFCC00]" /> Secure · powered by Emergent
              </div>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              <Stat value="India + US" label="Live markets" />
              <Stat value="GPT-5.2" label="Signal brain" />
              <Stat value="Adaptive" label="Learns daily" />
            </div>
          </div>

          {/* Right showcase */}
          <div className="relative">
            <div className="absolute inset-0 bg-[#FFCC00]/10 blur-3xl rounded-full" />
            <div className="relative border border-zinc-900 bg-[#0F0F0F] rounded-md p-5 overflow-hidden">
              <div className="flex items-center gap-2 pb-3 border-b border-zinc-900 mb-3">
                <span className="w-2 h-2 rounded-full bg-[#FFCC00] pulse-dot" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">// Live Signal · AAPL · 5m</span>
              </div>
              <FakeSignal />
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Feature icon={<Activity className="w-3.5 h-3.5 text-[#FFCC00]" />} text="Part 1 · India Intraday" />
                <Feature icon={<Zap className="w-3.5 h-3.5 text-[#F472B6]" />} text="Part 2 · US Intraday" />
                <Feature icon={<Brain className="w-3.5 h-3.5 text-emerald-400" />} text="Adaptive Learning" />
                <Feature icon={<TrendingUp className="w-3.5 h-3.5 text-[#60A5FA]" />} text="AI Trade Coach" />
              </div>
            </div>
          </div>
        </div>

        {/* What this platform does */}
        <div className="mt-14">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">// How it works</div>
          <div className="grid md:grid-cols-3 gap-4">
            <AboutCard n="01" title="Scan & filter the tape" body="Every 3 minutes the AI scans a liquid universe, then filters by liquidity, volatility and live news/events to find the cleanest directional setups." />
            <AboutCard n="02" title="Decide & manage the trade" body="A risk engine sets a precise entry, stop and two targets (R:R ≥ 1:2), sizes the position, tracks live P&L, and auto square-offs before the market close." />
            <AboutCard n="03" title="Learn & get sharper" body="A 9-stage adaptive-learning loop reviews every closed trade, a Challenger critic vetoes repeat mistakes, and winning patterns get promoted into future signals." />
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <AboutCard n="IN" title="Part 1 · India (NSE)" body="Same-day cash-equity intraday on NSE. Runs 09:15–15:30 IST with auto square-off at 3:15 PM IST. Prices via MoneyControl." accent="#FFCC00" />
            <AboutCard n="US" title="Part 2 · US (NASDAQ / NYSE)" body="Same-day US stocks & ETFs. Runs 09:30–16:00 ET with auto square-off at 3:55 PM ET. Prices via Yahoo Finance, news/event filter powered by a live feed." accent="#F472B6" />
          </div>
        </div>

        <div className="mt-12 text-xs font-mono text-zinc-600 text-center">
          Built for India + US intraday traders. Trading involves risk of capital loss — assistive analysis, not financial advice.
        </div>
      </div>
    </div>
  );
}

function AboutCard({ n, title, body, accent = "#60A5FA" }) {
  return (
    <div className="border border-zinc-900 bg-[#0F0F0F] rounded-md p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-mono text-xs font-bold px-2 py-0.5 rounded-sm" style={{ color: accent, background: `${accent}12`, border: `1px solid ${accent}30` }}>{n}</span>
        <span className="font-display text-sm font-semibold text-white">{title}</span>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-mono text-2xl font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    </div>
  );
}

function Feature({ icon, text }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-black/40 border border-zinc-900 rounded-sm text-xs text-zinc-300">
      {icon}{text}
    </div>
  );
}

function FakeSignal() {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="px-3 py-1 rounded-sm bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] font-mono text-xs font-semibold">LONG</div>
        <div className="font-mono text-[10px] text-zinc-500">CONV 82% · R:R 1:2</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[{l:"Entry",v:"$509.0",c:"#FFFFFF"},{l:"SL",v:"$504.9",c:"#FF3B30"},{l:"TP2",v:"$517.2",c:"#00E676"}].map(x=> (
          <div key={x.l} className="border border-zinc-900 rounded-sm bg-black/40 p-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{x.l}</div>
            <div className="font-mono text-base font-semibold" style={{color:x.c}}>{x.v}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500">
        <span>QTY 491</span><span>NOTIONAL $250K</span><span>R:R 1:2</span>
      </div>
    </div>
  );
}
